package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.apply
import dev.lingvo.shared.screenshots.geom.toParent
import dev.lingvo.shared.screenshots.geom.inverse
import dev.lingvo.shared.screenshots.geom.times

/** Flattening a rigid group preserves its children's world centres, rotations and handedness. */
internal fun ungroupBox(parent: Box, child: Box): Box {
    val center = parent.toParent().apply(Point(child.x + child.w / 2, child.y + child.h / 2))
    return child.copy(x = center.x - child.w / 2, y = center.y - child.h / 2,
        rotation = parent.rotation + child.rotation * if (parent.flipX) -1 else 1,
        flipX = parent.flipX != child.flipX)
}

/** Handle math changes boxes only; a text layer's font and fit specification remain untouched. */
internal fun resizeGesture(
    box: Box, handle: dev.lingvo.shared.screenshots.geom.Handle, delta: Point,
    aspectDefault: Boolean, shift: Boolean = false, alt: Boolean = false, alwaysAspect: Boolean = false,
): Box {
    val west = handle.name.contains("W"); val east = handle.name.contains("E")
    val north = handle.name.contains("N"); val south = handle.name.contains("S")
    val factor = if (alt) 2 else 1
    var width = (box.w + (if (east) delta.x else if (west) -delta.x else 0.0) * factor).coerceAtLeast(1.0)
    var height = (box.h + (if (south) delta.y else if (north) -delta.y else 0.0) * factor).coerceAtLeast(1.0)
    if (alwaysAspect || aspectDefault != shift) {
        if ((east || west) && (!(north || south) || kotlin.math.abs(width / box.w - 1) >= kotlin.math.abs(height / box.h - 1))) {
            height = width * box.h / box.w
        } else width = height * box.w / box.h
    }
    val dx = if (alt || !east && !west) (box.w - width) / 2 else if (west) box.w - width else 0.0
    val dy = if (alt || !north && !south) (box.h - height) / 2 else if (north) box.h - height else 0.0
    // Preserve the opposite handle in parent coordinates even on a rotated or reflected box.
    val center = box.toParent().apply(Point(dx + width / 2, dy + height / 2))
    return box.copy(x = center.x - width / 2, y = center.y - height / 2, w = width, h = height)
}

/** Proportional selection resize scales positions about one shared union instead of each layer's centre. */
internal fun scaleSelection(boxes: Map<String, Box>, union: dev.lingvo.shared.screenshots.geom.Rect,
    resized: Box): Map<String, Box> = boxes.mapValues { (_, box) ->
    box.copy(x = resized.x + (box.x - union.x) * resized.w / union.w,
        y = resized.y + (box.y - union.y) * resized.h / union.h,
        w = box.w * resized.w / union.w, h = box.h * resized.h / union.h)
}

/** Reparenting preserves rigid world geometry without rounding away a nested group's rotation. */
internal fun reparentBox(box: Box, from: dev.lingvo.shared.screenshots.render.Affine,
    to: dev.lingvo.shared.screenshots.render.Affine): Box {
    val matrix = requireNotNull(to.inverse()) * from * box.toParent()
    val center = matrix.apply(Point(box.w / 2, box.h / 2))
    val flipped = matrix.a * matrix.d - matrix.b * matrix.c < 0
    val angle = kotlin.math.atan2(if (flipped) -matrix.b else matrix.b, if (flipped) -matrix.a else matrix.a) * 180 / kotlin.math.PI
    return box.copy(x = center.x - box.w / 2, y = center.y - box.h / 2, rotation = angle, flipX = flipped)
}

/** Parent rotations change local deltas, while keyboard and pointer moves stay on document axes. */
internal fun moveBoxesInDocument(page: dev.lingvo.shared.screenshots.doc.Page, boxes: Map<String, Box>, delta: Point): Map<String, Box> =
    boxes.filterKeys { id -> pathTo(page.layers, id).dropLast(1).none { it.base.id in boxes } }.mapValues { (id, box) ->
        val parent = pathTo(page.layers, id).dropLast(1).lastOrNull()?.let {
            dev.lingvo.shared.screenshots.geom.worldOf(page, it.base.id)
        } ?: dev.lingvo.shared.screenshots.render.Affine.IDENTITY
        val inverse = requireNotNull(parent.inverse())
        val origin = inverse.apply(Point(0.0, 0.0))
        val end = inverse.apply(delta)
        box.copy(x = box.x + end.x - origin.x, y = box.y + end.y - origin.y)
    }

/** The editor's three-degree cardinal threshold is wider than the engine's general two-degree helper. */
internal fun editorSnapAngle(degrees: Double, forced: Boolean): Double {
    require(degrees.isFinite())
    val normalized = (degrees % 360 + 360) % 360
    if (forced) return (kotlin.math.round(normalized / 15) * 15) % 360
    val cardinal = (kotlin.math.round(normalized / 90) * 90) % 360
    val distance = minOf(kotlin.math.abs(normalized - cardinal), 360 - kotlin.math.abs(normalized - cardinal))
    return if (distance <= 3) cardinal else normalized
}

/** A group has no scale matrix in v1, so resizing its frame must bake scale into descendant boxes. */
internal fun withResizedBox(layer: dev.lingvo.shared.screenshots.doc.Layer, box: Box): dev.lingvo.shared.screenshots.doc.Layer {
    val resized = layer.withBase(layer.base.copy(box = box))
    if (resized !is dev.lingvo.shared.screenshots.doc.GroupLayer || box.w == layer.base.box.w && box.h == layer.base.box.h) return resized
    val sx = box.w / layer.base.box.w
    val sy = box.h / layer.base.box.h
    require(kotlin.math.abs(sx - sy) < 1e-8 || allLayers(resized.children).none { it is dev.lingvo.shared.screenshots.doc.DeviceLayer }) {
        "Group resizing must preserve device frame aspect ratios" // i18n-ignore: internal edit validation
    }
    fun scale(layers: List<dev.lingvo.shared.screenshots.doc.Layer>): List<dev.lingvo.shared.screenshots.doc.Layer> = layers.map {
        val old = it.base.box
        val next = it.withBase(it.base.copy(box = old.copy(x = old.x * sx, y = old.y * sy, w = old.w * sx, h = old.h * sy)))
        if (next is dev.lingvo.shared.screenshots.doc.GroupLayer) next.copy(children = scale(next.children)) else next
    }
    return resized.copy(children = scale(resized.children))
}
